import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './AuditLog.css';
import { useAuth } from '../../context/AuthContext';

export default function AuditLog() {
  const { can, canAccess } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [table, setTable] = useState('');
  const [act, setAct] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  React.useEffect(() => {
    const d = new Date();
    const prev = new Date(d.getTime() - 7 * 86400 * 1000);
    setFrom(prev.toISOString().slice(0, 10));
    setTo(d.toISOString().slice(0, 10));
  }, []);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.audit.list({ from, to, table, act }) : Promise.resolve({ rows: [], tables: [], actions: [] }),
    [from, to, table, act],
    { rows: [], tables: [], actions: [], count: 0 }
  );

  const { data: stats } = useApi(
    () => from && to ? api.audit.stats(from, to) : Promise.resolve({ by_action: [], by_table: [], daily_trend: [] }),
    [from, to], { by_action: [], by_table: [], daily_trend: [] }
  );

  if (!canAccess('audit_logs')) {
    return (
      <div className="audit-log-page">
        <ErrBanner msg="Access Denied: You do not have permission to access audit logs." />
      </div>
    );
  }

  const handleDelete = async () => {
    setSaving(true);
    try {
      if (confirm.isBulk) {
        await api.audit.bulkRemove(Array.from(selectedIds));
        notify(`✅ ${selectedIds.size} logs deleted`);
        setSelectedIds(new Set());
      } else {
        await api.audit.remove(confirm.id);
        notify('✅ Audit log deleted');
      }
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
    setConfirm(null);
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === (data.rows || []).length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set((data.rows || []).map(r => r.id)));
    }
  };

  const actionColor = (a) => ({
    CREATE: '#1e6e3b',
    UPDATE: '#1a6fad',
    DELETE: '#991b1b',
    CANCEL: '#92400e',
  })[a] || '#4a5568';

  return (
    <>
      <div className="audit-log-page">
        <div className="page-header">
          <h2 className="page-title">Audit Log</h2>
        </div>

        {/* Forbidden message if backend responds 403 */}

        {error && !error.includes('Admin') && <ErrBanner msg={error} />}

        <div className="al-filters">
          <div className="form-group">
            <label>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group">
            <label>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Table</label>
            <select value={table} onChange={e => setTable(e.target.value)}>
              <option value="">All</option>
              {(data.tables || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Action</label>
            <select value={act} onChange={e => setAct(e.target.value)}>
              <option value="">All</option>
              {(data.actions || []).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
        </div>

        {/* Stats KPIs */}
        {!loading && stats.by_action.length > 0 && (
          <div className="al-stats">
            {stats.by_action.map(s => (
              <div key={s.action} className="al-stat" style={{ borderLeftColor: actionColor(s.action) }}>
                <div className="al-stat-label">{s.action}</div>
                <div className="al-stat-value">{s.count}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? <Spinner /> : (
          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Audit entries ({data.count})</span>
              {can('audit_logs', 'delete') && selectedIds.size > 0 && (
                <button className="btn btn-primary btn-xs" style={{ background: 'var(--danger)' }}
                  onClick={() => setConfirm({ isBulk: true })}>
                  🗑️ Delete Selected ({selectedIds.size})
                </button>
              )}
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: 'center' }}>
                    <input type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === (data.rows || []).length}
                      onChange={toggleSelectAll} />
                  </th>
                  <th style={{ textAlign: 'left' }}>When</th>
                  <th style={{ textAlign: 'left' }}>Action</th>
                  <th style={{ textAlign: 'left' }}>Table</th>
                  <th>Record ID</th>
                  <th style={{ textAlign: 'left' }}>Details</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No audit entries</td></tr>
                ) : data.rows.map(r => (
                  <React.Fragment key={r.id}>
                    <tr className={`al-row ${selectedIds.has(r.id) ? 'selected' : ''}`}
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleSelect(r.id)} />
                      </td>
                      <td style={{ textAlign: 'left' }}>{fmtDate(r.performed_at, true)}</td>
                      <td style={{ textAlign: 'left' }}>
                        <span className="al-action-pill" style={{ background: actionColor(r.action) }}>
                          {r.action}
                        </span>
                      </td>
                      <td style={{ textAlign: 'left' }}>{r.table_name}</td>
                      <td>#{r.record_id}</td>
                      <td style={{ textAlign: 'left' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                          {expanded === r.id ? '▼ Hide' : '▶ Show diff'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        {can('audit_logs', 'delete') && (
                          <button className="btn btn-sm" style={{ color: 'var(--danger)', padding: '2px 6px' }} onClick={() => setConfirm(r)}>
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr>
                        <td colSpan="7" style={{ padding: 0 }}>
                          <div className="al-diff">
                            <div className="al-diff-col">
                              <div className="al-diff-head">Old data</div>
                              <pre>{formatJson(r.old_data)}</pre>
                            </div>
                            <div className="al-diff-col">
                              <div className="al-diff-head">New data</div>
                              <pre>{formatJson(r.new_data)}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirm}
        title={confirm?.isBulk ? "Bulk Delete Logs?" : "Delete Audit Log?"}
        message={confirm?.isBulk
          ? `Are you sure you want to delete ${selectedIds.size} selected audit entries? This action cannot be undone.`
          : `Are you sure you want to delete this audit entry? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel="Delete"
      />
    </>
  );
}

function formatJson(raw) {
  if (!raw) return '—';
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return JSON.stringify(parsed, null, 2);
  } catch { return String(raw); }
}
